import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { absUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Download, Upload, Calendar, Heart, Plane, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const TABS = [
    { id: "bookings", label: t("dashboard.tab_bookings"), icon: Plane },
    { id: "consults", label: t("dashboard.tab_consults"), icon: Calendar },
    { id: "wishlist", label: t("dashboard.tab_wishlist"), icon: Heart },
    { id: "documents", label: t("dashboard.tab_documents"), icon: FileText },
  ];
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [consults, setConsults] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("passport");

  const refresh = async () => {
    try { setBookings((await api.get("/bookings/me")).data || []); } catch {}
    try { setConsults((await api.get("/consultations/me")).data || []); } catch {}
    try { setWishlist((await api.get("/wishlist")).data || []); } catch {}
    try { setDocs((await api.get("/documents/me")).data || []); } catch {}
  };

  useEffect(() => { refresh(); }, []);

  const payBalance = async (b) => {
    try {
      const r = await api.post("/payments/checkout", {
        booking_id: b.booking_id,
        origin_url: window.location.origin,
        payment_kind: "balance",
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("document_type", docType);
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      refresh();
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const download = async (d) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/download/${d.document_id}`;
    window.open(url, "_blank");
  };

  const joinIfReady = (c) => {
    const now = new Date();
    const t = new Date(c.scheduled_time);
    const diff = (t - now) / 60000; // minutes
    return diff <= 10 && diff >= -60;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="pt-28 pb-24 max-w-6xl mx-auto px-6 md:px-12" data-testid="dashboard-page">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="t-overline text-seafoam mb-4">{t("dashboard.welcome")}</div>
            <h1 className="t-display text-slate-900"><b>{user?.name?.split(" ")[0] || "Traveller"}.</b></h1>
          </div>
          {user?.role === "partner" && <Link to="/partner/portal" data-testid="link-partner-portal" className="btn-pill bg-white text-slate-900 border border-slate-200">{t("dashboard.partner_portal")}</Link>}
        </div>

        <div className="mt-12 flex flex-wrap gap-2" data-testid="dash-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-2 rounded-full text-sm flex items-center gap-2 transition-colors ${tab === t.id ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"}`}
            >
              <t.icon size={14} strokeWidth={1.5} />
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "bookings" && (
            <div className="grid md:grid-cols-2 gap-6" data-testid="bookings-tab">
              {bookings.length === 0 && <div className="text-slate-500">{t("dashboard.no_bookings")} <Link to="/tours" className="text-seafoam underline">{t("dashboard.browse_tours")}</Link>.</div>}
              {bookings.map((b) => {
                const statusBadge = {
                  paid: "bg-emerald-50 text-emerald-700",
                  deposit_paid: "bg-sky-50 text-sky-700",
                  pending: "bg-amber-50 text-amber-700",
                }[b.status] || "bg-slate-50 text-slate-600";
                return (
                  <div key={b.booking_id} className="bg-white rounded-2xl overflow-hidden shadow-sm flex flex-col sm:flex-row">
                    <div className="sm:w-32 shrink-0 bg-slate-100 h-32 sm:h-auto"><img src={absUrl(b.tour_cover)} alt="" className="w-full h-full object-cover" /></div>
                    <div className="p-5 flex-1">
                      <div className="t-overline text-slate-500">{b.start_date} · {b.participants} pax</div>
                      <div className="text-lg font-medium text-slate-900 mt-1"><b>{b.tour_title}</b></div>
                      <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                        <span className="text-sm text-slate-700">${b.amount_usd.toLocaleString()}</span>
                        <span className={`text-xs px-3 py-1 rounded-full ${statusBadge}`}>{(b.status || "").replace("_", " ")}</span>
                      </div>
                      {b.status === "deposit_paid" && (b.balance_amount || 0) > 0 && (
                        <button
                          data-testid={`pay-balance-${b.booking_id}`}
                          onClick={() => payBalance(b)}
                          className="mt-3 btn-pill bg-slate-900 text-white hover:bg-slate-800 text-xs"
                        >
                          {t("dashboard.pay_balance")} · ${b.balance_amount.toLocaleString(undefined, {maximumFractionDigits: 2})}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "consults" && (
            <div className="grid md:grid-cols-2 gap-6" data-testid="consults-tab">
              {consults.length === 0 && <div className="text-slate-500">No consultations booked. <Link to="/consultations" className="text-seafoam underline">Book one</Link>.</div>}
              {consults.map((c) => {
                const ready = joinIfReady(c);
                return (
                  <div key={c.consultation_id} className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="text-xs uppercase tracking-[0.25em] text-slate-500">{new Date(c.scheduled_time).toLocaleString()}</div>
                    <div className="text-lg font-medium text-slate-900 mt-1">{c.topic}</div>
                    <div className="text-sm text-slate-500 mt-1">Via {c.contact_method}: {c.contact_value}</div>
                    {c.notes && <div className="text-sm text-slate-600 mt-3">"{c.notes}"</div>}
                    <button
                      data-testid={`consult-join-${c.consultation_id}`}
                      disabled={!ready}
                      className={`mt-4 btn-pill text-sm ${ready ? "bg-seafoam text-white hover:bg-[#7A988F]" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
                    >
                      {ready ? t("dashboard.join_ready") : t("dashboard.awaiting_host")}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "wishlist" && (
            <div className="grid md:grid-cols-3 gap-6" data-testid="wishlist-tab">
              {wishlist.length === 0 && <div className="text-slate-500">{t("dashboard.no_wishlist")}</div>}
              {wishlist.map((tw) => (
                <Link key={tw.tour_id} to={`/tours/${tw.slug}`} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:-translate-y-1 transition-transform">
                  <div className="aspect-[4/3]"><img src={absUrl(tw.cover_image_url)} alt="" className="w-full h-full object-cover" /></div>
                  <div className="p-4">
                    <div className="t-overline text-seafoam">{tw.destination}</div>
                    <div className="text-base font-medium mt-1 text-slate-900">{tw.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {tab === "documents" && (
            <div data-testid="docs-tab">
              <div className="bg-white rounded-2xl p-6 shadow-sm flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <select value={docType} onChange={(e) => setDocType(e.target.value)} className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm">
                  <option value="passport">Passport copy</option>
                  <option value="visa">Visa</option>
                  <option value="request">Special request</option>
                </select>
                <label className="btn-pill bg-slate-900 text-white hover:bg-slate-800 cursor-pointer">
                  <Upload size={14} /> {t("dashboard.upload")}
                  <input data-testid="doc-upload-input" type="file" onChange={onUpload} className="hidden" />
                </label>
                <div className="text-sm text-slate-500">Files stored privately. Admin can also upload e-tickets here.</div>
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-4">
                {docs.length === 0 && <div className="text-slate-500">{t("dashboard.no_docs")}</div>}
                {docs.map((d) => (
                  <div key={d.document_id} className="bg-white rounded-2xl p-5 shadow-sm flex items-center justify-between">
                    <div>
                      <div className="t-overline text-slate-500">
                        {d.uploaded_by_admin ? t("dashboard.from_team") : d.document_type}
                      </div>
                      <div className="text-base font-medium text-slate-900 mt-1">{d.file_name}</div>
                      <div className="text-xs text-slate-400 mt-1">{new Date(d.created_at).toLocaleDateString()}</div>
                    </div>
                    <button data-testid={`doc-download-${d.document_id}`} onClick={() => download(d)} className="btn-pill bg-white border border-slate-200 text-slate-800 text-sm">
                      <Download size={14} /> {t("dashboard.download")}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
